import { call, put, takeLatest, takeEvery, all, select } from 'redux-saga/effects';
import {
  FETCH_TRACKING_RECORDS_REQUEST,
  FETCH_TRACKING_STATS_REQUEST,
  fetchTrackingRecordsSuccess,
  fetchTrackingRecordsFailure,
  fetchTrackingStatsSuccess,
  fetchTrackingStatsFailure
} from './liveUrlTrackingActions';
import liveUrlTrackingApi from '../utils/liveUrlTrackingApi';

// Selector to get current state
export const getLiveUrlTrackingState = (state) => state.liveUrlTracking;

// Worker Sagas
export function* fetchTrackingRecordsSaga() {
  try {
    // Get current filters and pagination from state
    const state = yield select(getLiveUrlTrackingState);
    
    const filters = {
      rootFolder: state.filters?.rootFolder || [],
      pushedBy: state.filters?.pushedBy || '',
      dateFrom: state.filters?.dateFrom || null,
      dateTo: state.filters?.dateTo || null,
      fileType: state.filters?.fileType || [],
      sortBy: state.filters?.sortBy || 'pushedAt',
      sortOrder: state.filters?.sortOrder || 'desc'
    };

    const pagination = {
      currentPage: state.pagination?.currentPage || 1,
      pageSize: state.pagination?.pageSize || 20
    };
    
    const response = yield call(
      liveUrlTrackingApi.fetchTrackingRecords, 
      filters, 
      pagination
    );

    const records = response?.data?.records || [];
    const paginationData = response?.data?.pagination || {
      currentPage: 1,
      totalPages: 0,
      totalRecords: 0,
      pageSize: 20
    };

    yield put(fetchTrackingRecordsSuccess(records, paginationData));
    
  } catch (error) {
    const errorPayload = formatError(error, 'FETCH_RECORDS_ERROR');
    yield put(fetchTrackingRecordsFailure(errorPayload));
  }
}

export function* fetchTrackingStatsSaga() {
  try {
    // Get current filters from state (no pagination needed for stats)
    const state = yield select(getLiveUrlTrackingState);
    
    const filters = {
      rootFolder: state.filters?.rootFolder || [],
      pushedBy: state.filters?.pushedBy || '',
      dateFrom: state.filters?.dateFrom || null,
      dateTo: state.filters?.dateTo || null,
      fileType: state.filters?.fileType || []
    };
    
    const response = yield call(
      liveUrlTrackingApi.fetchTrackingStats, 
      filters
    );

    const statistics = response?.data || {};
    yield put(fetchTrackingStatsSuccess(statistics));
    
  } catch (error) {
    const errorPayload = formatError(error, 'FETCH_STATS_ERROR');
    yield put(fetchTrackingStatsFailure(errorPayload));
  }
}

// Helper function to format errors consistently
function formatError(error, defaultCode) {
  // Handle HTTP errors
  if (error.response) {
    return {
      message: error.response.data?.message || error.message,
      code: 'HTTP_ERROR',
      status: error.response.status
    };
  }
  
  // Handle timeout errors
  if (error.code === 'ECONNABORTED') {
    return {
      message: 'Request timeout - please try again',
      code: 'TIMEOUT_ERROR'
    };
  }
  
  // Handle network errors
  if (error.code === 'NETWORK_ERROR') {
    return {
      message: 'Network connection failed - please check your internet connection',
      code: 'NETWORK_ERROR'
    };
  }
  
  // Handle generic errors
  return {
    message: error.message || 'An unexpected error occurred',
    code: defaultCode
  };
}

// Watcher Sagas
export function* watchFetchTrackingRecords() {
  yield takeLatest(FETCH_TRACKING_RECORDS_REQUEST, fetchTrackingRecordsSaga);
}

export function* watchFetchTrackingStats() {
  yield takeEvery(FETCH_TRACKING_STATS_REQUEST, fetchTrackingStatsSaga);
}

// Root Saga
export function* liveUrlTrackingSaga() {
  yield all([
    takeLatest(FETCH_TRACKING_RECORDS_REQUEST, fetchTrackingRecordsSaga),
    takeEvery(FETCH_TRACKING_STATS_REQUEST, fetchTrackingStatsSaga)
  ]);
}

export default liveUrlTrackingSaga;